// Mobile bottom tab bar — thumb-reachable primary navigation for the phone
// shell. Four direct tabs (Chat home + the user-chosen Email / Calendar /
// Settings) plus a "More" menu exposing the full nav set. Desktop uses TopNav.
import { Calendar, MessageSquare, MoreHorizontal, Settings } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@onestone/ui/dropdown-menu';
import { AgentMailLogo } from '@/components/AgentMailLogo';
import { api } from '@/lib/api';
import { cn } from '@onestone/ui/utils';
import { NAV_BADGE_POLL_MS } from '@/lib/uiConstants';
import { NAV } from './nav';

interface Tab {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const TABS: Tab[] = [
  { to: '/chat', label: 'Chat', icon: MessageSquare },
  { to: '/email', label: 'Email', icon: AgentMailLogo as unknown as ComponentType<SVGProps<SVGSVGElement>> },
  { to: '/calendar', label: 'Calendar', icon: Calendar },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const tabClass = (isActive: boolean): string =>
  cn(
    'flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-2xs font-medium transition-colors',
    isActive ? 'text-primary' : 'text-muted-foreground',
  );

export function BottomTabBar() {
  const navigate = useNavigate();

  const emailStatus = useQuery<{ configured: boolean; unreadCount?: number | null }>({
    queryKey: ['email-status'],
    queryFn: () => api('/api/email/status'),
    refetchInterval: NAV_BADGE_POLL_MS,
    retry: false,
  });
  const unreadCount = emailStatus.data?.unreadCount ?? 0;

  return (
    <nav
      aria-label="Primary"
      data-testid="bottom-tab-bar"
      className="shrink-0 border-t border-border bg-background/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex items-stretch">
        {TABS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`tab-${to.replace('/', '')}`}
            className={({ isActive }) => tabClass(isActive)}
          >
            {({ isActive }) => (
              <>
                <span className="relative">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                  {label === 'Email' && unreadCount > 0 ? (
                    <span className="absolute -right-2 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-1 text-2xs font-semibold leading-none text-primary-foreground">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  ) : null}
                </span>
                <span className={cn(isActive && 'font-semibold')}>{label}</span>
              </>
            )}
          </NavLink>
        ))}

        <DropdownMenu>
          <DropdownMenuTrigger className={tabClass(false)}>
            <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
            <span>More</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="mb-1 max-h-[60vh] overflow-y-auto">
            <DropdownMenuLabel>All sections</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {NAV.map(({ to, label, icon: Icon }) => (
              <DropdownMenuItem key={to} onSelect={() => navigate(to)}>
                <Icon className="mr-2 h-4 w-4" aria-hidden="true" />
                {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
